import React, { createContext, useContext, useState, useCallback } from 'react'
import { useHistory } from 'react-router-dom'
import IMotoristaReferencia from '../domain/entities/IMotoristaReferencia'

export interface IAddress {
  cep?: string
  logradouro?: string
  numero?: string
  complemento?: string
  bairro?: string
  cidade?: string
  estado?: string
  cidade_ibge?: string
}

interface ICreateMotoristas {
  doc_name?: string
  telefone?: string
  doc_token_sdk?: string
  doc_imagem_frente?: string
  doc_imagem_verso?: string
  endereco?: IAddress
  motorista_referencia?: IMotoristaReferencia[]
  email?: string
}

interface ICreateMotoristasContextState {
  motoristas: ICreateMotoristas
  setMotoristas(dto: ICreateMotoristas): void
  clearMotorista(): void
  step: number
  setStep: (value: number) => void
  numberOfSteps: number
  handleNextButton: () => void
  handleBackButton: () => void
  loading: boolean
  setLoading: (value: boolean) => void
}

interface ICreateMotoristasState {
  motoristas: ICreateMotoristas
}

const MotoristasContext = createContext<ICreateMotoristasContextState>(
  {} as ICreateMotoristasContextState,
)

export function useMotoristas(): ICreateMotoristasContextState {
  const context = useContext(MotoristasContext)

  if (!context) {
    throw new Error('useMotoristas must be used within an AuthProvider')
  }

  return context
}

export const MotoristasProvider: React.FC = ({ children }) => {
  const history = useHistory()
  const [data, setData] = useState<ICreateMotoristasState>(
    {} as ICreateMotoristasState,
  )

  const [step, setStep] = useState(0)
  const [loading, setLoading] = useState(false)

  const numberOfSteps =
    Object.keys({
      Document: 0,
      Phone: 1,
      Email: 2,
      Address: 3,
      ReferenciasProfissinais: 4,
    }).length - 1

  const setMotoristas = useCallback(
    (dto: ICreateMotoristas) => {
      setData({ motoristas: { ...data.motoristas, ...dto } })
    },
    [data.motoristas],
  )

  const clearMotorista = useCallback(() => {
    setData({} as ICreateMotoristasState)
  }, [])

  const handleBackButtonValue = useCallback(() => {
    setStep(step => {
      const newStep = step - 1

      if (newStep < 0) {
        history.push('/motoristas')

        return 0
      }

      return step - 1
    })
  }, [history])

  const handleNextButtonValue = () => {
    setStep(step => {
      const newStep = step + 1

      if (newStep > numberOfSteps) {
        return step
      }

      return newStep
    })
  }

  const setStepValue = (value: number) => {
    setStep(value)
  }

  const setLoadingValue = (value: boolean) => {
    setLoading(value)
  }

  return (
    <MotoristasContext.Provider
      value={{
        motoristas: data.motoristas,
        setMotoristas,
        clearMotorista,
        step,
        setStep: setStepValue,
        numberOfSteps,
        handleNextButton: handleNextButtonValue,
        handleBackButton: handleBackButtonValue,
        loading,
        setLoading: setLoadingValue,
      }}
    >
      {children}
    </MotoristasContext.Provider>
  )
}
